import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SortableRowProps {
  item: Record<string, any>;
  fields: string[];
  onEdit: (item: Record<string, any>) => void;
  onDelete: (id: number) => void;
  id: number;
}

function SortableRow({ item, fields, onEdit, onDelete, id }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-gray-50/50 ${isDragging ? "bg-white shadow-lg opacity-90" : ""}`}
    >
      <td className="px-3 py-3">
        <button
          {...attributes}
          {...listeners}
          className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-300 cursor-grab active:cursor-grabbing transition-colors"
        >
          <i className="ri-draggable text-sm" />
        </button>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
        #{item.id}
      </td>
      {fields.slice(0, 3).map((field) => (
        <td
          key={field}
          className="px-4 py-3 text-sm text-gray-700 max-w-[200px] truncate"
        >
          {typeof item[field] === "boolean"
            ? item[field]
              ? "Sí"
              : "No"
            : item[field] ?? "—"}
        </td>
      ))}
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            onClick={() => onEdit(item)}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-primary hover:text-primary transition-colors cursor-pointer"
            title="Editar"
          >
            <i className="ri-pencil-line text-sm" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-red-400 hover:text-red-500 transition-colors cursor-pointer"
            title="Eliminar"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </td>
    </tr>
  );
}

interface SortableTableProps {
  data: Record<string, any>[];
  fields: string[];
  onEdit: (item: Record<string, any>) => void;
  onDelete: (id: number) => void;
  onReorder: (newData: Record<string, any>[]) => void;
  hasOrderField: boolean;
}

export default function SortableTable({
  data,
  fields,
  onEdit,
  onDelete,
  onReorder,
  hasOrderField,
}: SortableTableProps) {
  const [items, setItems] = useState(data);

  // Keep items in sync when data changes
  useEffect(() => {
    setItems(data);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);
    onReorder(newItems);
  };

  if (!hasOrderField) {
    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left text-xs font-medium text-gray-600 uppercase tracking-wider px-4 py-3">
                  ID
                </th>
                {fields.slice(0, 3).map((field) => (
                  <th
                    key={field}
                    className="text-left text-xs font-medium text-gray-600 uppercase tracking-wider px-4 py-3"
                  >
                    {field}
                  </th>
                ))}
                <th className="text-right text-xs font-medium text-gray-600 uppercase tracking-wider px-4 py-3">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                    #{item.id}
                  </td>
                  {fields.slice(0, 3).map((field) => (
                    <td
                      key={field}
                      className="px-4 py-3 text-sm text-gray-700 max-w-[200px] truncate"
                    >
                      {typeof item[field] === "boolean"
                        ? item[field]
                          ? "Sí"
                          : "No"
                        : item[field] ?? "—"}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => onEdit(item)}
                        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-primary hover:text-primary transition-colors cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-pencil-line text-sm" />
                      </button>
                      <button
                        onClick={() => onDelete(item.id)}
                        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-red-400 hover:text-red-500 transition-colors cursor-pointer"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-sm text-gray-400"
                  >
                    No hay registros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-12 px-3 py-3">
                    <i className="ri-drag-move-2-line text-gray-400 text-sm" />
                  </th>
                  <th className="text-left text-xs font-medium text-gray-600 uppercase tracking-wider px-4 py-3">
                    ID
                  </th>
                  {fields.slice(0, 3).map((field) => (
                    <th
                      key={field}
                      className="text-left text-xs font-medium text-gray-600 uppercase tracking-wider px-4 py-3"
                    >
                      {field}
                    </th>
                  ))}
                  <th className="text-right text-xs font-medium text-gray-600 uppercase tracking-wider px-4 py-3">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <SortableRow
                    key={item.id}
                    id={item.id}
                    item={item}
                    fields={fields}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-sm text-gray-400"
                    >
                      No hay registros.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}